import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { Platform, ModalController } from '@ionic/angular';
import * as Leaflet from 'leaflet';
import 'leaflet-markers-canvas';
import 'leaflet.markercluster';

import { ERouteDef } from 'src/app/app-utils';
import { GeneralCache } from 'src/app/classes/app/general-cache';
import { ICustomFilterOption } from 'src/app/classes/def/app/filters';
import { EAppIcons, EAppIconsStandard, EMarkerIcons } from 'src/app/classes/def/app/icons';
import { Messages } from 'src/app/classes/def/app/messages';
import { ThemeColors } from 'src/app/classes/def/app/theme';
import { EStoryUnlockType } from 'src/app/classes/def/business/story';
import { EStoryListMode, IStoriesPageResponse, IStory, IStoryUnlock } from 'src/app/classes/def/core/story';
import { INavParams, IViewSpecs } from 'src/app/classes/def/nav-params/general';
import { IMarketplaceDetailNavParams } from 'src/app/classes/def/nav-params/marketplace';
import { ENavParamsResources } from 'src/app/classes/def/nav-params/resources';
import { IPageResponse } from 'src/app/classes/def/requests/general';
import { ResourceManager } from 'src/app/classes/general/resource-manager';
import { NavParamsService, INavParamsInfo } from 'src/app/services/app/nav-params';
import { MarketplaceDataService } from 'src/app/services/data/marketplace';
import { AnalyticsService } from 'src/app/services/general/apis/analytics';
import { SettingsManagerService } from 'src/app/services/general/settings-manager';
import { BackButtonService } from 'src/app/services/general/ui/back-button';
import { MarketplaceContentPage } from '../content/content.page';
import { UiExtensionService } from 'src/app/services/general/ui/ui-extension';
import { IMarkerContainer } from './map';
import { ICanvasMarkerContainer, MarkerUtils } from 'src/app/services/map/marker-utils';
import { StringUtils } from 'src/app/services/app/utils/string-utils';


@Component({
  selector: 'app-marketplace-map',
  templateUrl: './map.page.html',
  styleUrls: ['./map.page.scss'],
})
export class MarketplaceMapPage implements OnInit, OnDestroy {

  appIcons = EAppIcons;
  appIconsStandard = EAppIconsStandard;

  map: Leaflet.Map;
  markPoint: Leaflet.Marker;
  markersCanvas: any;
  markers: IMarkerContainer<IStory>[] = [];
  markerClusterGroup: Leaflet.MarkerClusterGroup;

  title: string = "Stories";
  theme: string = "theme-aubergine theme-aubergine-bg";

  np: INavParams = null;
  vs: IViewSpecs;

  isModal: boolean = false;
  animate: boolean = true;

  autocompleteQuery: string = "";
  lastQuery: string = null;

  showState: string = "inactive";

  selectedCategoryCode: number = EStoryListMode.published;
  orderBy: number = GeneralCache.orderBy.stories;
  sort: number = GeneralCache.sort.stories;
  filter: ICustomFilterOption[] = GeneralCache.filter.stories;

  stories: IStory[] = [];

  loaded: boolean = true;
  loading: boolean = false;

  showMapDiv: boolean = false;
  locked: boolean = false;

  zoomOutLevel: number = 2;
  standalone: boolean = false;

  timeouts = {
    loading: null,
    search: null,
    showMapDiv: null,
    loadLeafletMap: null,
    goToStory: null
  };


  constructor(
    private settingsProvider: SettingsManagerService,
    private router: Router,
    private plt: Platform,
    private backButton: BackButtonService,
    private analytics: AnalyticsService,
    private modalCtrl: ModalController,
    private route: ActivatedRoute,
    private marketplaceDataProvider: MarketplaceDataService,
    private nps: NavParamsService,
    private uiext: UiExtensionService
  ) {

  }

  ngOnInit() {
    this.analytics.trackView("browse");
    this.settingsProvider.getSettingsLoaded(false).then((res) => {
      if (res) {
        this.theme = ThemeColors.theme[SettingsManagerService.settings.app.settings.theme.value].css;
        // this.theme = Constants.theme["aubergine"].css;
      }
    }).catch((err: Error) => {
      console.error(err);
    });

    console.log("activated route: ", this.route.snapshot.queryParams);

    this.nps.checkParamsLoaded().then(() => {
      let npInfo: INavParamsInfo = this.nps.getCombined(ENavParamsResources.marketplaceBrowse, null, this.np);
      console.log("nav params: ", npInfo.params);
      let npar = npInfo.params;
      let hasParams: boolean = npInfo.hasParams;
      console.log(npar);

      if (hasParams) {
        let np: INavParams = npar;
        console.log(np);
        this.vs = np.view;
        this.isModal = this.vs ? this.vs.isModal : false;
        if (this.isModal) {
          this.animate = false;
          this.timeouts.loading = setTimeout(() => {
            this.showState = 'active';
          }, 250);
        }
      } else {
        this.route.queryParams.subscribe((params: Params) => {
          if (params) {
            // params in stringified form to object form
            this.standalone = params.standalone as any === "true";
          }
        });
      }

      this.initLeafletMap().then(() => {
        this.loadStoriesMode();
      })

      this.plt.ready().then(() => {
        this.backButton.replace(() => {
          this.back();
        });
      }).catch((err: Error) => {
        console.error(err);
      });
    }).catch((err: Error) => {
      console.error(err);
    });
  }


  async loadStoriesMode() {
    try {
      let pageResponse: IPageResponse;
      let storyData: IStoriesPageResponse;
      let releaseFilter: number[] = this.filter.map(ut => {
        if (ut.checked) {
          return ut.code;
        } else {
          return null;
        }
      }).filter(ut => ut !== null);
      // await this.uiext.showLoadingV2Queue("Loading..");
      this.loading = true;
      let page: number = null;
      storyData = await this.marketplaceDataProvider.listStories(null, null, null, null, releaseFilter, this.orderBy, this.sort, true, page);
      pageResponse = {
        page: storyData.page,
        pages: storyData.pages,
        aux: null,
        data: storyData.stories
      };
      this.stories = pageResponse.data;
      this.showStories(this.stories);
      // await this.uiext.dismissLoadingV2();
      this.loading = false;
    } catch (err) {
      // await this.uiext.dismissLoadingV2();
      this.loading = false;
      this.uiext.showAlertNoAction(Messages.msg.requestFailed.after.msg, Messages.msg.requestFailed.after.sub);
      console.error(err);
    }
  }

  public showStories(stories: IStory[]): void {
    console.log("show stories: ", stories);
    this.removeMarkers();
    for (let story of stories) {
      let lat: number = null;
      let lng: number = null;
      if (story == null) {
        console.warn("undefined story");
        continue;
      }
      if (story.city != null) {
        lat = story.city.lat;
        lng = story.city.lng;
      }
      if (story.locs != null && story.locs.length > 0 && story.locs[0] && story.locs[0].merged) {
        lat = story.locs[0].merged.lat;
        lng = story.locs[0].merged.lng;
      }
      if (lat == null) {
        console.warn("undefined coords");
        continue;
      }
      // this.addCanvasMarker(lat, lng, story.name, story);
      this.addMarker(lat, lng, story.name, story);
    }
  }


  /**
  * finally enter selected story
  * @param story 
  */
  enterStory(story: IStory) {
    if (this.locked) {
      console.warn("open in progress");
      return;
    }
    // get search token
    let token: string = null;
    if (story.storyUnlocks != null) {
      let su: IStoryUnlock = story.storyUnlocks.find(su => su.type === EStoryUnlockType.searchOnly);
      if (su != null) {
        token = su.unlockCode;
      } else {
        su = story.storyUnlocks.find(su => su.type === EStoryUnlockType.searchOnlyShort);
        if (su != null) {
          token = su.unlockCode;
        }
      }
    }
    let params: IMarketplaceDetailNavParams = {
      storyId: story.id,
      fromMarket: false,
      reload: true
    };

    if (token != null) {
      params.token = token;
    }
    this.locked = true;
    let promiseView: Promise<any>;
    if (this.animate) {
      this.showState = "inactive";
    }
    promiseView = this.uiext.showCustomModal(null, MarketplaceContentPage, {
      view: {
        fullScreen: true,
        transparent: false,
        large: true,
        addToStack: true,
        frame: true
      },
      storyId: story.id,
      token: token,
      params: params
    });

    promiseView.then(() => {
      this.locked = false;
    }).catch((err: Error) => {
      this.uiext.showAlertNoAction(Messages.msg.storyNotAvailable.after.msg, Messages.msg.storyNotAvailable.after.sub);
      console.error(err);
      this.locked = false;
    });
  }


  ngOnDestroy() {
    this.timeouts = ResourceManager.clearTimeoutObj(this.timeouts);
    this.removeMap();
  }

  removeMap() {
    if (this.map != null) {
      this.map.off();
      this.map.remove();
      this.map = null;
    }
  }

  initLeafletMap() {
    return new Promise((resolve) => {
      if (!this.showMapDiv) {
        this.timeouts.showMapDiv = setTimeout(() => {
          this.showMapDiv = true;
          this.timeouts.loadLeafletMap = setTimeout(() => {
            this.initLeafletMapCore();
            resolve(true);
          }, 1000);
        }, 1000);
      } else {
        this.initLeafletMapCore();
        resolve(true);
      }
    });
  }


  /**
   * 
      https://leaflet-extras.github.io/leaflet-providers/preview/
      https://leafletjs.com/plugins.html#basemap-providers
  
      var OpenStreetMap_Mapnik = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      });
  
      var Esri_WorldGrayCanvas = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}', {
        attribution: 'Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ',
        maxZoom: 16
      });
  
      var Esri_WorldTopoMap = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}', {
        attribution: 'Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ, TomTom, Intermap, iPC, USGS, FAO, NPS, NRCAN, GeoBase, Kadaster NL, Ordnance Survey, Esri Japan, METI, Esri China (Hong Kong), and the GIS User Community'
      });
  
       L.tileLayer('https://api.mapbox.com/v4/mapbox.streets/{z}/{x}/{y}.vector.pbf?access_token={accessToken}', {
          attribution: '© Mapbox',
          maxZoom: 14,
          accessToken: 'your-mapbox-access-token'
      }).addTo(map);
      
   * @param lat 
   * @param lng 
   */
  initLeafletMapCore() {
    if (!this.map) {
      this.map = new Leaflet.Map('mapId');
      // let accessToken: string = 'pk.eyJ1IjoiYWxleHAyNSIsImEiOiJjaXVxcm5jZngwMDBqMnRvMnNkcnN4ZDV2In0.s_HFMSs5rj1IQMtC1r7Z9w';
      // 'https://api.mapbox.com/styles/v1/mapbox/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}'
      // 'https://api.mapbox.com/v4/{tileset_id}/{zoom}/{x}/{y}.{format}'
      // 'https://api.mapbox.com/v4/{tileset_id}/{zoom}/{x}/{y}.pbf'
      // 'https://api.mapbox.com/styles/v1/mapbox/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}'
      Leaflet.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
        maxNativeZoom: 19,
        // id: "mapbox.mapbox-streets-v8",
        // accessToken: accessToken
      }).addTo(this.map);


      this.markersCanvas = new Leaflet.MarkersCanvas().addTo(this.map);
      this.markerClusterGroup = Leaflet.markerClusterGroup({
        iconCreateFunction: (cluster) => {
          var markers = cluster.getAllChildMarkers();
          var html = '<div class="cluster-icon">' + markers.length + '</div>';
          return Leaflet.divIcon({ html: html, className: 'custom-cluster-icon', iconSize: Leaflet.point(42, 42) });
        },
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: true,
        zoomToBoundsOnClick: true,
        animate: true,
        animateAddingMarkers: true,
        chunkedLoading: true
      });
      this.map.addLayer(this.markerClusterGroup);
    }
    this.zoomOut();
    this.map.on("dragend", (event: Leaflet.DragEndEvent) => {
      if (event != null) {
        console.log("map dragend: ", this.map.getCenter());
      }
    });
    this.map.on("zoomend", (event: Leaflet.LeafletEvent) => {
      if (event != null) {
        console.log("map zoomend: ", this.map.getZoom());
      }
    });
  }

  zoomOut() {
    this.map.setView([45, 0], this.zoomOutLevel);
  }

  removeRefefenceMarker() {
    if (this.markPoint != null) {
      this.markPoint.removeFrom(this.map);
      this.markPoint = null;
    }
  }

  addReferenceMarker(lat: number, lng: number, label: string) {
    if (!this.markPoint) {
      // Icon options
      var iconOptions: Leaflet.IconOptions = {
        iconUrl: EMarkerIcons.locationPin,
        iconSize: [50, 50]
      };
      // Creating a custom icon
      var customIcon = Leaflet.icon(iconOptions);
      var markerOptions: Leaflet.MarkerOptions = {
        title: label,
        icon: customIcon,
        draggable: true
      };
      this.markPoint = Leaflet.marker([lat, lng], markerOptions);

      // let popupHtml: string = "<h2>" + label + "</h2>" + `
      //   <button class="detach" style="width:100%">Detach</button>
      // `;
      // this.markPoint.bindPopup(popupHtml).on("popupopen", (a) => {
      //   var popUp = a.target.getPopup()
      //   popUp.getElement().querySelector(".detach").addEventListener("click", (_e) => {
      //     this.deselectAutocompleteItems();
      //     this.setAutocompletePlaceSelectedCustomCoords(null, null);
      //     this.selectedPlace.autocompletePlace = null;
      //     this.selectedPlace.placeResult = null;
      //   });
      // });

      this.markPoint.on('dragend', (event: Leaflet.DragEndEvent) => {
        var marker: Leaflet.Marker = event.target;
        var position: Leaflet.LatLng = marker.getLatLng();
        this.setCustomLoc(position.lat, position.lng);
      });

      this.markPoint.on('click', (event: Leaflet.LeafletMouseEvent) => {
        var marker: Leaflet.Marker = event.target;
        console.log("clicked marker: ", marker);
        this.showPlaceSelector();
      });

      // this.map.addLayer(this.markPoint);

      this.markPoint.addTo(this.map);
      this.map.setView([lat, lng], 13);

      this.map.on('click', (e) => {
        if (e && e.latlng) {
          this.setCustomLoc(e.latlng.lat, e.latlng.lng);
        }
      });
    } else {
      let coords: Leaflet.LatLngExpression = new Leaflet.LatLng(lat, lng);
      this.markPoint.setLatLng(coords);
    }
  }

  removeMarkers() {
    for (let marker of this.markers) {
      try {
        marker.marker.removeFrom(this.map);
      } catch (err) {
        console.error(err);
      }
    }
    this.markers = [];
  }

  addMarker(lat: number, lng: number, label: string, story: IStory) {
    console.log("add marker: ", lat, lng, label);
    // Icon options
    var iconOptions: Leaflet.IconOptions = {
      iconUrl: EMarkerIcons.locationPin,
      iconSize: [50, 50]
    };
    // Creating a custom icon
    var customIcon = Leaflet.icon(iconOptions);
    var markerOptions: Leaflet.MarkerOptions = {
      title: label,
      icon: customIcon,
      draggable: true
    };
    let marker: Leaflet.Marker<any> = Leaflet.marker([lat, lng], markerOptions);
    let mc: IMarkerContainer<IStory> = {
      marker: marker,
      data: story
    };

    // Adding a tooltip to display the title above the marker
    marker.bindTooltip(label, { permanent: true, direction: 'top', offset: [0, -20] });

    this.markers.push(mc);

    marker.on('click', (event: Leaflet.LeafletMouseEvent) => {
      var marker: Leaflet.Marker = event.target;
      console.log("clicked marker: ", marker);
      this.enterStory(mc.data);
    });

    // marker.addTo(this.map);
    this.markerClusterGroup.addLayer(marker);
  }


  // Add this method to create a canvas marker
  addCanvasMarker(lat: number, lng: number, label: string, story: IStory) {
    this.loadCanvasMarkerData(EMarkerIcons.story, label, true).then((iconUrl: string) => {
      console.log("add marker: ", lat, lng, label);
      if (iconUrl != null) {
        let iconSize: number = 200;
        const icon = Leaflet.icon({
          iconUrl: iconUrl,
          iconSize: [iconSize, iconSize * 0.8],
          iconAnchor: [iconSize / 2, iconSize],
          popupAnchor: [0, -iconSize]
        });

        const marker: Leaflet.Marker<any> = Leaflet.marker([lat, lng], { icon, title: label });

        let mc: IMarkerContainer<IStory> = {
          marker: marker,
          data: story
        };

        this.markers.push(mc);
        marker.bindTooltip(label, { permanent: true, direction: 'top', offset: [0, -20] });
        marker.on('click', (event: Leaflet.LeafletMouseEvent) => {
          console.log("clicked marker: ", marker);
          this.enterStory(mc.data);
        });
        this.markersCanvas.addMarker(marker);
      } else {
        console.warn("could not load icon url for marker");
      }
    });
  }


  loadCanvasMarkerData(iconUrl: string, label: string, circularFrame: boolean): Promise<string> {
    let promise: Promise<string> = new Promise((resolve) => {
      let canvas = document.createElement('canvas');
      let f: ICanvasMarkerContainer = MarkerUtils.formatCanvasMarkerContainer(null, 120);
      canvas.width = f.fullW;
      canvas.height = f.fullH;
      MarkerUtils.setDPI(canvas, 300, true);
      let ctx = canvas.getContext('2d');
      let img = new Image();
      img.crossOrigin = "anonymous";
      img.src = iconUrl;

      img.onload = () => {
        // console.log("draw marker faded: ", opts.faded);
        if (circularFrame) {
          ctx = MarkerUtils.drawMarkerText(ctx, f, img, null, null, null, false, label, null, true, null);
        } else {
          ctx = MarkerUtils.drawMarkerTextPlain(ctx, f, img, null, null, null, false, label, null, true, null);
        }
        let icon: string = canvas.toDataURL();
        resolve(icon);
      };
      img.onerror = () => {
        resolve(null);
      };
    });
    return promise;
  }


  showPlaceSelector() {

  }

  setCustomLoc(lat: number, lng: number) {
    if (this.map && this.markPoint) {
      this.markPoint.setLatLng(new Leaflet.LatLng(lat, lng));
      this.map.panTo(new Leaflet.LatLng(lat, lng));
    }
  }


  back() {
    // this.showState = "inactive";
    // this.timeout = setTimeout(() => {
    //   this.navCtrl.setRoot(StoryCategoryListPage).then(() => {
    //   }).catch((err: Error) => {
    //     console.error(err);
    //   });
    // }, Constants.ANIMATION_TIME);

    if (this.isModal) {
      this.modalDismiss(null);
    } else {
      let navParams: INavParams = {
        view: null,
        params: null
      };
      this.nps.set(ENavParamsResources.home, navParams);
      this.router.navigate([ERouteDef.home], { replaceUrl: true }).then(() => {
      }).catch((err: Error) => {
        console.error(err);
      });
    }
  }

  modalDismiss(params: any) {
    setTimeout(() => {
      this.modalCtrl.dismiss(params).then(() => {
      }).catch((err: Error) => {
        console.error(err);
      });
    }, 1);
  }


  /**
   * on input changed
   */
  updateSearch() {
    if (this.autocompleteQuery === '' && this.lastQuery != null) {
      console.log("empty query / clear");
      this.zoomOut();
      return;
    }
    this.lastQuery = this.autocompleteQuery;
    console.log("query: ", this.autocompleteQuery);
    if (!this.stories) {
      return;
    }

    let threshold: number = 30;
    let input: string = this.autocompleteQuery.toLowerCase().trimEnd();
    if (input.length < 3) {
      return;
    }

    let zoomLevel: number = 15;

    console.log("input: ", input);

    // search by story name
    let suggestions = this.markers
      .filter(item => item.data.name.toLowerCase().startsWith(input))
      .map(item => ({ item, distance: StringUtils.computeLevenshteinDistance(input, item.data.name.toLowerCase()) }))
      .filter(obj => obj.distance <= threshold)
      .sort((a, b) => a.distance - b.distance)
      .map(obj => obj.item);

    if (suggestions.length === 0) {
      console.warn("no suggestions by story name");
      zoomLevel = 12;
      // search by city
      suggestions = this.markers
        .filter(item => item.data.city.name.toLowerCase().startsWith(input))
        .map(item => ({ item, distance: StringUtils.computeLevenshteinDistance(input, item.data.city.name.toLowerCase()) }))
        .filter(obj => obj.distance <= threshold)
        .sort((a, b) => a.distance - b.distance)
        .map(obj => obj.item);
    }

    if (suggestions.length === 0) {
      console.warn("no suggestions");
      return;
    }
    console.log("suggestions: ", suggestions);
    this.map.setView(suggestions[0].marker.getLatLng(), zoomLevel); // Adjust zoom level as needed
  }
}
